import type { FC } from 'react';
import type { Ply } from '../lib/types';
import { PACKING_EFFICIENCY, COLOUR_OPTIONS } from '../lib/constants';
import { s } from '../lib/styles';
import PlyLayer from './PlyLayer';

interface Props {
  plies: Ply[];
  onAdd: () => void;
  onRemove: () => void;
  onUpdate: (index: number, field: keyof Ply, value: string) => void;
  printRatePerKg: number;
  colourOptionIndex: number;
}

const PlyConfigurator: FC<Props> = ({
  plies,
  onAdd,
  onRemove,
  onUpdate,
  printRatePerKg,
  colourOptionIndex,
}) => {
  const linerCount = plies.filter((p) => p.type === 'liner').length;
  const fluteCount = plies.filter((p) => p.type === 'flute').length;
  const missingCount = plies.filter(
    (p) => !p.gsm || (p.type === 'flute' && !p.fluteType)
  ).length;

  return (
    <div style={s.card}>
      <div style={s.cardHeader}>
        <div>
          <span style={s.cardTitle}>Ply Structure</span>
          <div
            style={{ marginTop: 6, display: 'flex', gap: 5, flexWrap: 'wrap' }}
          >
            <span style={s.plyBadge}>{plies.length}-ply</span>
            <span
              style={{
                ...s.plyBadge,
                background: '#eff6ff',
                color: '#2563eb',
                borderColor: '#bfdbfe',
              }}
            >
              {linerCount} liners
            </span>
            <span
              style={{
                ...s.plyBadge,
                background: '#fffbeb',
                color: '#d97706',
                borderColor: '#fde68a',
              }}
            >
              {fluteCount} flutes
            </span>
            {missingCount > 0 && (
              <span
                style={{
                  ...s.plyBadge,
                  background: '#fff7ed',
                  color: '#ea580c',
                  borderColor: '#fed7aa',
                }}
              >
                ✦ {missingCount} → AI assumes
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          <button
            onClick={onRemove}
            disabled={plies.length <= 3}
            style={{ ...s.plyCtrlBtn, opacity: plies.length <= 3 ? 0.3 : 1 }}
          >
            − 2-ply
          </button>
          <button onClick={onAdd} style={s.plyCtrlBtn}>
            + 2-ply
          </button>
        </div>
      </div>

      <div style={s.plyDiagram}>
        {plies.map((ply, i) => (
          <PlyLayer key={i} ply={ply} index={i} onUpdate={onUpdate} />
        ))}
      </div>

      <div style={s.formulaBox}>
        <div
          style={{
            color: '#94a3b8',
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            marginBottom: 5,
          }}
        >
          Formula summary
        </div>
        <div
          style={{
            color: '#64748b',
            fontSize: 10,
            lineHeight: 1.9,
            fontFamily: 'monospace',
          }}
        >
          material/unit = weight_kg × (paper_rate + conversion)
          <br />
          volume = (L×W × thickness) × qty / {PACKING_EFFICIENCY} → cheapest
          vehicle
          <br />
          shipping/unit = vehicle_rate × distance_km / qty
          <br />
          print/unit = outer_liner_kg × ₹{printRatePerKg} ×{' '}
          {COLOUR_OPTIONS[colourOptionIndex].multiplier} (
          {COLOUR_OPTIONS[colourOptionIndex].label})<br />
          price/unit = material + shipping + print
        </div>
      </div>
    </div>
  );
};

export default PlyConfigurator;
